import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import type { DataNotificationRow, DataNotificationSeverity } from '@/integrations/supabase/payrollNotificationsTypes';

export interface DataNotificationsCounts {
  critical: number;
  this_week: number;
  low_priority: number;
}

function getStatusPriority(status: DataNotificationRow['status']): number {
  if (status === 'open') return 2;
  if (status === 'snoozed') return 1;
  return 0;
}

function dedupeNotifications(rows: DataNotificationRow[] | undefined): DataNotificationRow[] {
  if (!rows?.length) return [];

  const byEntity = new Map<string, DataNotificationRow>();

  for (const row of rows) {
    const entityKey = row.related_entity_id
      ? `${row.type}:${row.related_entity_type ?? 'unknown'}:${row.related_entity_id}`
      : `id:${row.id}`;

    const existing = byEntity.get(entityKey);
    if (!existing) {
      byEntity.set(entityKey, row);
      continue;
    }

    const rowPriority = getStatusPriority(row.status);
    const existingPriority = getStatusPriority(existing.status);

    if (rowPriority > existingPriority) {
      byEntity.set(entityKey, row);
      continue;
    }

    if (
      rowPriority === existingPriority &&
      new Date(row.updated_at).getTime() > new Date(existing.updated_at).getTime()
    ) {
      byEntity.set(entityKey, row);
    }
  }

  return Array.from(byEntity.values()).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

export function useDataNotifications() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['data-notifications'],
    queryFn: async (): Promise<DataNotificationRow[]> => {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return [];

      const now = new Date().toISOString();
      const { data, error } = await (supabase as any)
        .from('data_notifications')
        .select('*')
        .eq('company_id', companyId)
        .in('status', ['open', 'snoozed'])
        .or(`snoozed_until.is.null,snoozed_until.lt.${now}`)
        .order('created_at', { ascending: false })
        .limit(5000);

      if (error) throw error;
      return (data ?? []) as DataNotificationRow[];
    },
    staleTime: 2 * 60 * 1000,
  });

  const dedupedData = dedupeNotifications(query.data);

  const counts: DataNotificationsCounts = {
    critical: 0,
    this_week: 0,
    low_priority: 0,
  };
  if (dedupedData.length) {
    for (const n of dedupedData) {
      if (n.status !== 'open') continue;
      if (n.severity === 'critical') counts.critical++;
      else if (n.severity === 'this_week') counts.this_week++;
      else counts.low_priority++;
    }
  }

  const resolveMutation = useMutation({
    mutationFn: async ({
      notificationId,
      resolutionPayload,
    }: {
      notificationId: string;
      resolutionPayload?: Record<string, unknown>;
    }) => {
      const { error } = await (supabase as any)
        .from('data_notifications')
        .update({
          status: 'resolved',
          resolved_at: new Date().toISOString(),
          resolution_payload: resolutionPayload ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-unassigned'] });
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: async ({
      notificationId,
      snoozedUntil,
    }: {
      notificationId: string;
      snoozedUntil: string;
    }) => {
      const { error } = await (supabase as any)
        .from('data_notifications')
        .update({
          status: 'snoozed',
          snoozed_until: snoozedUntil,
          updated_at: new Date().toISOString(),
        })
        .eq('id', notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-notifications'] });
    },
  });

  return {
    data: dedupedData,
    isLoading: query.isLoading,
    error: query.error,
    counts,
    resolve: resolveMutation.mutateAsync,
    resolveMutation,
    snooze: snoozeMutation.mutateAsync,
    snoozeMutation,
    refetch: query.refetch,
  };
}
